// src/components/corkboard/CorkBoardStage.jsx
//
// The board itself: the cork surface, the cards pinned to it, and the strings
// between them.
//
// The stage owns two things the cards cannot own individually — the live
// position of whichever card is being dragged (so the strings stay tied to its
// pin while it moves) and the measured size of the surface (so a card can be
// clamped inside it). Everything else is passed down.
//
// The cork texture is CSS rather than an image: a few offset radial gradients
// at different sizes read as cork grain, cost nothing to load, and cannot go
// missing behind a CDN. Colours come from corkTokens.js, which derives them
// from the app's gold — see the note there about why the prototype's brown is
// not used.
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';

import { layoutUnplaced, stringsFor } from '../../utils/corkBoard';
import { CB, FONT } from './corkTokens';
import CorkCard from './CorkCard';
import CorkStrings from './CorkStrings';

// The grain. Six dots at coprime-ish sizes so the pattern does not visibly tile.
const CORK_TEXTURE = [
  'radial-gradient(circle at 15% 20%, rgba(0,0,0,0.08) 0 2px, transparent 2px)',
  'radial-gradient(circle at 45% 65%, rgba(0,0,0,0.07) 0 2px, transparent 2px)',
  'radial-gradient(circle at 75% 30%, rgba(0,0,0,0.08) 0 2px, transparent 2px)',
  'radial-gradient(circle at 85% 80%, rgba(0,0,0,0.07) 0 2px, transparent 2px)',
  'radial-gradient(circle at 25% 85%, rgba(0,0,0,0.06) 0 2px, transparent 2px)',
  'radial-gradient(circle at 60% 10%, rgba(0,0,0,0.06) 0 2px, transparent 2px)',
].join(', ');

export default function CorkBoardStage({
  nodes,
  edges,
  selectedId,
  showAllConnections,
  onSelect,
  onMoveNode,       // (nodeId, { x, y }) => void — the drop, which writes
  emptyState,       // rendered when the board has no elements at all
}) {
  const stageRef = useRef(null);
  const [live, setLive] = useState(null);   // { id, x, y } while a card is dragged
  const [size, setSize] = useState({ w: 0, h: 0 });

  // Measured, not computed. The stage is a grid cell whose width depends on the
  // side panel and the window; anything derived from a constant would clamp
  // cards against a board that is not the size of the board.
  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([entry]) => {
      const box = entry.contentRect;
      setSize({ w: box.width, h: box.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const positioned = useMemo(() => {
    // The measured width, so cards with no stored position land on the board
    // rather than past the right edge of a narrow one.
    const laid = layoutUnplaced(nodes, size.w);
    if (!live) return laid;
    // Overlay the dragged card's live position so the strings follow it. The
    // card is already drawing itself there from its own state; this is only so
    // the two ends of a string agree during the drag.
    return laid.map((n) => (String(n._id) === String(live.id)
      ? { ...n, x: live.x, y: live.y }
      : n));
  }, [nodes, live, size.w]);

  // Two related but different questions, which is why there are two refs here.
  // `selectedRef` decides which strings are *thickened* — a selected card's own
  // relationships stand out whatever the toggle says. `focusRef` decides what
  // is *dimmed*, and goes null when "show all connections" is on, which is what
  // that toggle means: stop fading the rest of the board.
  const selectedRef = selectedId ? `node:${selectedId}` : null;
  const focusRef = showAllConnections ? null : selectedRef;

  const strings = useMemo(
    () => stringsFor(edges, positioned, selectedRef),
    [edges, positioned, selectedRef],
  );

  const connectedIds = useMemo(() => {
    if (!focusRef) return null;
    const set = new Set();
    edges.forEach((e) => {
      if (e.from === focusRef) set.add(e.to);
      else if (e.to === focusRef) set.add(e.from);
    });
    return set;
  }, [edges, focusRef]);

  const handleDragMove = useCallback((id, pos) => {
    setLive(pos ? { id, ...pos } : null);
  }, []);

  return (
    <Box
      ref={stageRef}
      onPointerDown={(e) => {
        // A click on bare cork clears the selection. Checked against the target
        // rather than bound to the background element, because the strings layer
        // and the texture both sit between the cork and the pointer.
        if (e.target === e.currentTarget) onSelect?.(null);
      }}
      sx={{
        position: 'relative',
        overflow: 'auto',
        minHeight: '74vh',
        backgroundColor: CB.cork,
        backgroundImage: `${CORK_TEXTURE}, linear-gradient(rgba(0,0,0,0.04), rgba(0,0,0,0.04))`,
        backgroundSize: '38px 38px, 46px 46px, 34px 34px, 50px 50px, 40px 40px, 44px 44px, cover',
        borderRight: `1px solid ${CB.lineSoft}`,
        boxShadow: `inset 0 0 60px rgba(0,0,0,0.35)`,
        borderTop: `10px solid ${CB.corkFrame}`,
        borderBottom: `10px solid ${CB.corkFrame}`,
      }}
    >
      <CorkStrings
        strings={strings}
        width={size.w}
        height={size.h}
        hasSelection={Boolean(focusRef)}
      />

      {positioned.map((node) => (
        <CorkCard
          key={node._id}
          node={node}
          selected={String(node._id) === String(selectedId)}
          dimmed={Boolean(connectedIds) &&
            String(node._id) !== String(selectedId) &&
            !connectedIds.has(`node:${node._id}`)}
          stageRef={stageRef}
          onSelect={onSelect}
          onDragMove={handleDragMove}
          onMoved={onMoveNode}
        />
      ))}

      {nodes.length === 0 ? emptyState : (
        <Typography sx={{
          position: 'absolute', bottom: 14, left: '50%', transform: 'translateX(-50%)',
          fontFamily: FONT.mono, fontSize: 10.5, color: 'rgba(255,255,255,0.78)',
          backgroundColor: 'rgba(0,0,0,0.3)', px: 1.75, py: 0.75, borderRadius: '999px',
          pointerEvents: 'none',
        }}>
          Drag any card · click one to see its connections
        </Typography>
      )}
    </Box>
  );
}
